import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Button, Table, Input, FormGroup, Label } from 'reactstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  newFetchTeacherByCourseId,
  transactionCourseChange,
  changeLevel,
  getStudents,
} from '../../../../../Api/apiUtils';
import { PaymentConfigContext } from '../../../../../Api/global/ApiContext';
import { MixpanelEvents, trackEvent } from '../../../../../utils/mixpanelTracking';
import {
  calculateTotalPrice,
  buildToCourseDetail,
  buildSourcePayload,
  buildRemoveLevels,
  combineTransactions,
  firstFilledLevelId,
  changeErrorMessage,
  reportActiveLevelFailure,
  maxPurchasableClasses,
  reportChangeResult,
  sumRemainingClasses,
  sumRemainingValue,
  selectMenuPortalProps,
} from './txnChangeShared';
import { PanelProps } from './course-change-panel';
import TeacherSelect from './TeacherSelect';

interface ToStudent {
  studentId: string;
  userId: any;
  name: string;
  timezone?: string;
}

// Popup body for "Transfer to Student": move the selected transactions' remaining classes to
// ANOTHER student. The new course is priced for the To student; the transfer is blocked when
// that currency differs from the course's. The leftover goes to the To student's wallet and the
// new transaction (the only one refundable from now on) belongs to the To student.
const CourseTransferPanel: React.FC<PanelProps> = ({ transactions, studentId, profileData, userType, onDone }) => {
  const transaction = useMemo(() => combineTransactions(transactions), [transactions]);
  const paymentConfig = useContext(PaymentConfigContext);
  const paymentCourses: any[] = paymentConfig?.onlineCourseAcademic2 || [];

  const [submitting, setSubmitting] = useState(false);
  const [toStudent, setToStudent] = useState<ToStudent | null>(null);
  const [studentQuery, setStudentQuery] = useState('');
  const [studentResults, setStudentResults] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPaymentCourse, setSelectedPaymentCourse] = useState<{ value: string; label: string } | null>(null);
  const [toCourseDetail, setToCourseDetail] = useState<any>(null);
  const [pricingError, setPricingError] = useState('');
  const [teachers, setTeachers] = useState<any[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<any>('');
  const searchTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);

  useEffect(() => {
    if (!selectedPaymentCourse) return;
    (async () => {
      try {
        const response = await newFetchTeacherByCourseId({ page: 0, size: 500, courseId: selectedPaymentCourse.value });
        setTeachers(Array.isArray(response?.data?.content) ? response.data.content : []);
      } catch (e) {
        setTeachers([]);
      }
    })();
  }, [selectedPaymentCourse]);

  // Price the chosen course for the To student (re-priced when either changes).
  useEffect(() => {
    setToCourseDetail(null);
    setPricingError('');
    if (!toStudent || !selectedPaymentCourse) return;
    if (!toStudent.timezone) {
      setPricingError("The To student's timezone is missing, so their currency can't be checked.");
      return;
    }
    (async () => {
      try {
        const detail = await buildToCourseDetail(paymentCourses, selectedPaymentCourse.value, paymentConfig, toStudent.timezone as string);
        if (detail) setToCourseDetail(detail);
      } catch (e: any) {
        console.error(e);
        setPricingError(e?.message || 'Failed to fetch course price');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toStudent, selectedPaymentCourse]);

  const availableAmount = +sumRemainingValue(transaction).toFixed(2);
  const selectedLevelId = firstFilledLevelId(toCourseDetail);
  const activeLevelName = toCourseDetail?.levelList?.find((l: any) => l.levelId === selectedLevelId)?.level || '';
  const remainingClasses = sumRemainingClasses(transaction);
  const newCourseValue = toCourseDetail ? +(+toCourseDetail.discountedTotal).toFixed(2) : 0;
  const walletAmount = Math.max(0, +(availableAmount - newCourseValue).toFixed(2));
  const currencyMismatch = !!toCourseDetail && !!transaction.currency && toCourseDetail.currency !== transaction.currency;
  const overBudget = newCourseValue > availableAmount + 0.5;

  const handleStudentSearch = (value: string) => {
    setStudentQuery(value);
    setToStudent(null);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (value.trim().length < 2) {
      setShowDropdown(false);
      return;
    }
    setShowDropdown(true);
    searchTimer.current = setTimeout(async () => {
      let payload: any = { name: value };
      if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/gi.test(value)) payload = { emailId: value };
      else if (/^\+?\d+$/.test(value)) payload = { mobileNumber: value.replace(/^\+91|^\+1|^\+44/, '').replace(/\D/g, '') };
      try {
        const res = await getStudents(0, 20, payload);
        const list = Array.isArray(res) ? res : (res?.content || []);
        setStudentResults(list.filter((s: any) => String(s.studentId) !== String(studentId)));
      } catch (e) {
        setStudentResults([]);
      }
    }, 600);
  };

  const pickStudent = (s: any) => {
    setToStudent({ studentId: String(s.studentId), userId: s.userId, name: s.studentName, timezone: s.timezone || s.timeZone });
    setStudentQuery(s.studentName);
    setShowDropdown(false);
  };

  const handleClassCount = (levelId: string, value: string) => {
    const sanitized = value.replace(/[^0-9]/g, '');
    const newLevelList = toCourseDetail.levelList.map((l: any) =>
      l.levelId === levelId ? { ...l, classCount: +sanitized } : l
    );
    setToCourseDetail(calculateTotalPrice({ ...toCourseDetail, levelList: newLevelList }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!toStudent) return toast.error('Select the student to transfer to');
    if (!selectedPaymentCourse || !toCourseDetail) return toast.error('Select the new course');
    if (currencyMismatch) {
      return toast.error(`Course currency (${transaction.currency}) and the To student's currency (${toCourseDetail.currency}) are different. Transfer is not allowed.`);
    }
    if (!selectedTeacher?.value) return toast.error('Select a teacher');
    if (newCourseValue <= 0) return toast.error('Add at least one class to the new course');
    if (overBudget) return toast.error('New course value exceeds the available amount (partial top-up is not allowed)');

    const addLevels = toCourseDetail.levelList
      .filter((l: any) => l.classCount !== '' && +l.classCount !== 0)
      .map((l: any) => {
        const discount = +((+l.unit_price * +l.classCount * toCourseDetail.discountPercentage) / 100).toFixed(2);
        const finalAmount = +((+l.unit_price * +l.classCount) - discount).toFixed(2);
        return {
          level: l.level,
          levelId: l.levelId,
          courseId: l.courseId,
          courseName: l.courseName,
          noOfClasses: +l.classCount,
          unit_price: l.unit_price,
          totalDiscount: discount,
          totalFinalPrice: finalAmount,
        };
      });
    const removeLevels = buildRemoveLevels(transactions);

    const fromCourseId = transaction.courseList?.[0]?.courseId;
    const Userdetails = JSON.parse(localStorage.getItem('profileData') || '{}');

    const payload = {
      studentId,
      userId: profileData?.userId,
      name: profileData?.firstName || transaction.name,
      fromStudentName: profileData?.firstName || transaction.name,
      email: transaction.email || profileData?.emailId,
      toStudentId: toStudent.studentId,
      toUserId: toStudent.userId,
      toStudentName: toStudent.name,
      currency: transaction.currency,
      customer_id: transaction.customer_id,
      centreId: transaction.centreId || '',
      place_of_supply: transaction.place_of_supply || '',
      userType: userType || transaction.studentType || 'PAID',
      conversionRate: transaction.conversionRate,
      changedBy: Userdetails?.firstName || '',
      adminUser: Userdetails?.firstName || '',
      isFreeClass: false,
      teacherId: selectedTeacher.value,
      activeLevelId: selectedLevelId,
      activeLevel: activeLevelName,
      totalMovedClasses: addLevels.reduce((acc: number, l: any) => acc + l.noOfClasses, 0),
      totalRemovedClasses: removeLevels.reduce((acc: number, l: any) => acc + l.noOfClasses, 0),
      remainingClasses,
      remainingValue: availableAmount,
      newCourseValue,
      walletAmount,
      fromCourse: {
        courseId: fromCourseId,
        courseName: transaction.courseList?.[0]?.courseName || '',
        removeLevels,
      },
      toCourse: {
        courseId: selectedPaymentCourse.value,
        courseName: toCourseDetail.levelList?.[0]?.courseName || '',
        addLevels,
      },
      sources: transactions.map(buildSourcePayload),
    };

    setSubmitting(true);
    try {
      const response = await transactionCourseChange(payload);
      if (!response || response.success === false) {
        toast.error('Failed to transfer course');
        trackEvent(MixpanelEvents.COURSE_TRANSFER, {
          page: 'Transaction Course Transfer', studentId, toStudentId: toStudent.studentId,
          previousCourseId: fromCourseId, courseId: selectedPaymentCourse.value, status: 'failed',
        });
        return;
      }
      // Set the To student's active level in the new course.
      try {
        await changeLevel({ userId: toStudent.userId, courseId: selectedPaymentCourse.value, levelId: selectedLevelId, changeType: 'level' });
      } catch (e) {
        console.error('changeLevel failed', response?.traceId, e);
        reportActiveLevelFailure(response, activeLevelName);
      }
      trackEvent(MixpanelEvents.COURSE_TRANSFER, {
        page: 'Transaction Course Transfer', studentId, userId: profileData?.userId,
        toStudentId: toStudent.studentId, previousCourseId: fromCourseId, courseId: selectedPaymentCourse.value,
        newTransactionId: response.newTransactionId, walletAmount, status: 'success',
      });
      reportChangeResult(response, `Course transferred to ${toStudent.name}!`);
      onDone();
    } catch (error: any) {
      console.error('transaction course transfer error', error);
      toast.error(changeErrorMessage(error, 'Failed to transfer course'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h6>Available amount: <b>{availableAmount.toFixed(2)} {transaction.currency}</b> ({remainingClasses} classes)</h6>

      <FormGroup style={{ position: 'relative' }}>
        <Label>Transfer to student</Label>
        <Input
          type="text"
          placeholder="Search by name, email or mobile number"
          value={studentQuery}
          onChange={(e) => handleStudentSearch(e.target.value)}
        />
        {showDropdown && studentResults.length > 0 && (
          <div style={{ position: 'absolute', zIndex: 1000, background: 'white', border: '1px solid #ccc', borderRadius: 4, maxHeight: 220, overflowY: 'auto', width: '100%', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            {studentResults.map((s: any) => (
              <div
                key={s.studentId}
                onClick={() => pickStudent(s)}
                style={{ padding: 10, cursor: 'pointer', borderBottom: '1px solid #eee' }}
              >
                {s.studentName}
                <span style={{ color: '#888', fontSize: '0.85em' }}> · ID {s.studentId}{s.emailId ? ` · ${s.emailId}` : ''}</span>
              </div>
            ))}
          </div>
        )}
        {toStudent && (
          <div style={{ marginTop: 6, padding: 6, background: '#e9ecef', borderRadius: 4, fontSize: 14 }}>
            To student: <b>{toStudent.name}</b> (ID {toStudent.studentId})
          </div>
        )}
      </FormGroup>

      <FormGroup>
        <Label>New Course</Label>
        <Select
          {...selectMenuPortalProps}
          value={selectedPaymentCourse}
          onChange={(v: any) => { setSelectedPaymentCourse(v); setSelectedTeacher(''); }}
          options={paymentCourses.map((c) => ({ value: c.paidCourseId, label: c.name }))}
          placeholder="Select a course"
        />
      </FormGroup>

      {pricingError && <div style={{ color: 'red', marginBottom: 10 }}>{pricingError}</div>}
      {currencyMismatch && (
        <div style={{ color: 'red', marginBottom: 10 }}>
          Course currency ({transaction.currency}) and the To student's currency ({toCourseDetail.currency}) are different. Transfer is not allowed.
        </div>
      )}

      {toCourseDetail && !currencyMismatch && (
        <>
          <Table bordered>
            <thead>
              <tr>
                <th>Level</th>
                <th>Class Price</th>
                <th title="Most classes of this level that still fit in the available amount, after the classes entered for the other levels">Max Affordable Classes</th>
                <th>Class Count</th>
              </tr>
            </thead>
            <tbody>
              {toCourseDetail.levelList.map((l: any) => (
                <tr key={l.levelId}>
                  <td>{l.level}</td>
                  <td>{l.unit_price}</td>
                  <td>{maxPurchasableClasses(toCourseDetail, l.levelId, availableAmount)}</td>
                  <td style={{ width: 120 }}>
                    <Input type="text" value={l.classCount} onChange={(e) => handleClassCount(l.levelId, e.target.value)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <FormGroup>
            <Label>Teacher</Label>
            <TeacherSelect teachers={teachers} value={selectedTeacher} onChange={setSelectedTeacher} requirePaidEnabled />
          </FormGroup>

          <div style={{ marginTop: 10, padding: 10, background: '#f7f7f7', borderRadius: 8 }}>
            <div>New course value: <b>{newCourseValue.toFixed(2)} {toCourseDetail.currency}</b></div>
            <div>Active level (first level with classes): <b>{activeLevelName || '-'}</b></div>
            <div>Wallet credit to {toStudent?.name} (leftover): <b>{walletAmount.toFixed(2)} {toCourseDetail.currency}</b></div>
            <div style={{ color: '#6c757d', fontSize: '0.85rem' }}>After the transfer, refunds are only possible from {toStudent?.name}'s profile.</div>
            {overBudget && (
              <div style={{ color: 'red' }}>New course value exceeds available amount — not allowed.</div>
            )}
          </div>

          <Button
            color="primary"
            style={{ marginTop: 15 }}
            disabled={submitting || newCourseValue <= 0 || overBudget}
            onClick={handleSubmit}
          >
            {submitting ? 'Processing…' : 'Transfer Course'}
          </Button>
        </>
      )}
    </div>
  );
};

export default CourseTransferPanel;
